import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MaintenanceSchedule = ({ equipment, onRefresh }) => {
  const [viewMode, setViewMode] = useState('upcoming');

  const getMaintenanceSchedule = () => {
    const today = new Date();
    const schedule = equipment
      ?.filter((eq) => eq?.next_maintenance_date)
      ?.map((eq) => ({
        ...eq,
        daysUntil: Math.ceil((new Date(eq?.next_maintenance_date) - today) / (1000 * 60 * 60 * 24))
      }))
      ?.sort((a, b) => a?.daysUntil - b?.daysUntil);

    if (viewMode === 'upcoming') {
      return schedule?.filter((eq) => eq?.daysUntil >= 0);
    } else if (viewMode === 'overdue') {
      return schedule?.filter((eq) => eq?.daysUntil < 0);
    }
    return schedule;
  };

  const maintenanceSchedule = getMaintenanceSchedule();
  const overdueCount = equipment?.filter((eq) => {
    if (!eq?.next_maintenance_date) return false;
    return new Date(eq?.next_maintenance_date) < new Date();
  })?.length || 0;

  const getUrgencyColor = (daysUntil) => {
    if (daysUntil < 0) return 'text-error';
    if (daysUntil <= 7) return 'text-error';
    if (daysUntil <= 14) return 'text-warning';
    return 'text-muted-foreground';
  };

  return (
    <div className="space-y-6">
      {/* View Mode Selector */}
      <div className="flex items-center gap-3">
        <Button
          variant={viewMode === 'upcoming' ? 'primary' : 'outline'}
          size="sm"
          onClick={() => setViewMode('upcoming')}
        >
          Upcoming
        </Button>
        <Button
          variant={viewMode === 'overdue' ? 'primary' : 'outline'}
          size="sm"
          onClick={() => setViewMode('overdue')}
        >
          Overdue ({overdueCount})
        </Button>
        <Button
          variant={viewMode === 'all' ? 'primary' : 'outline'}
          size="sm"
          onClick={() => setViewMode('all')}
        >
          All
        </Button>
      </div>

      {/* Maintenance Schedule List */}
      <div className="bg-card rounded-xl border border-border">
        <div className="p-6 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Icon name="Calendar" size={20} />
            Maintenance Schedule
          </h3>
        </div>
        <div className="divide-y divide-border">
          {maintenanceSchedule?.map((eq) => (
            <div key={eq?.id} className="p-4 hover:bg-muted/30 transition-colors">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="font-semibold text-foreground">{eq?.name}</h4>
                    {eq?.daysUntil < 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-error/10 text-error">
                        <Icon name="AlertCircle" size={12} />
                        Overdue
                      </span>
                    )}
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">
                      <Icon name="MapPin" size={14} className="inline mr-1" />
                      {eq?.location}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      <Icon name="Package" size={14} className="inline mr-1" />
                      {eq?.manufacturer} {eq?.model}
                    </p>
                    {eq?.last_maintenance_date && (
                      <p className="text-sm text-muted-foreground">
                        <Icon name="Clock" size={14} className="inline mr-1" />
                        Last maintenance: {new Date(eq?.last_maintenance_date)?.toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-foreground mb-1">
                    {new Date(eq?.next_maintenance_date)?.toLocaleDateString()}
                  </p>
                  <p className={`text-xs font-medium ${getUrgencyColor(eq?.daysUntil)}`}>
                    {eq?.daysUntil < 0
                      ? `${Math.abs(eq?.daysUntil)} days overdue`
                      : eq?.daysUntil === 0
                      ? 'Today'
                      : eq?.daysUntil === 1
                      ? 'Tomorrow'
                      : `${eq?.daysUntil} days`}
                  </p>
                  <Button variant="outline" size="sm" className="mt-3" iconName="Wrench" iconPosition="left">
                    Log Maintenance
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {maintenanceSchedule?.length === 0 && (
        <div className="bg-card rounded-xl p-12 border border-border text-center">
          <Icon name="Calendar" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Maintenance Scheduled</h3>
          <p className="text-muted-foreground">
            {viewMode === 'overdue' ?'No overdue maintenance items' :'All equipment maintenance is up to date'}
          </p>
        </div>
      )}
    </div>
  );
};

export default MaintenanceSchedule;